import React, { useEffect, useState, useCallback } from "react";
import { adminApi } from "../apiAdmin";

interface DJ {
  _id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  slug?: string;
  pricePerRequest?: number;
  currency?: string;
}

interface Establishment {
  _id: string;
  name: string;
  slug: string;
  qrCodeId: string;
  djs: DJ[];
  createdAt: string;
}

function fmtAvatar(url?: string): string | null {
  if (!url) return null;
  return url.startsWith("http") ? url : `https://api.requesta.app${url}`;
}

const CLIENT_URL = window.location.origin;

export default function AdminEstablishmentsTab({ allDjs }: { allDjs: DJ[] }) {
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [newName, setNewName] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [addDjId, setAddDjId] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchEstablishments = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/establishments");
      setEstablishments(data);
    } catch {
      /* */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEstablishments();
  }, [fetchEstablishments]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await adminApi.post("/establishments", { name: newName.trim() });
      setNewName("");
      fetchEstablishments();
    } catch (err: any) {
      alert(err.response?.data?.msg || "Error creating establishment");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this establishment? This cannot be undone.")) return;
    try {
      await adminApi.delete(`/establishments/${id}`);
      setEstablishments((prev) => prev.filter((e) => e._id !== id));
      if (expanded === id) setExpanded(null);
    } catch (err: any) {
      alert(err.response?.data?.msg || "Error");
    }
  };

  const handleAddDj = async (estId: string) => {
    if (!addDjId) return;
    try {
      const { data } = await adminApi.post(`/establishments/${estId}/djs`, { djId: addDjId });
      setEstablishments((prev) => prev.map((e) => (e._id === estId ? data : e)));
      setAddDjId("");
    } catch (err: any) {
      alert(err.response?.data?.msg || "Error adding DJ");
    }
  };

  const handleRemoveDj = async (estId: string, djId: string) => {
    try {
      const { data } = await adminApi.delete(`/establishments/${estId}/djs/${djId}`);
      setEstablishments((prev) => prev.map((e) => (e._id === estId ? data : e)));
    } catch (err: any) {
      alert(err.response?.data?.msg || "Error");
    }
  };

  const getQrUrl = (slug: string) => `${CLIENT_URL}/venue/${slug}`;

  const getQrImageUrl = (slug: string) =>
    `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(getQrUrl(slug))}`;

  if (loading) return <p className="muted-text" style={{ padding: 30, textAlign: "center" }}>Loading...</p>;

  return (
    <section className="fade-in">
      {/* Create form */}
      <div className="adm-card" style={{ marginBottom: 20 }}>
        <h2 className="adm-section-title" style={{ marginBottom: 14 }}>Create Establishment</h2>
        <form onSubmit={handleCreate} style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input
            className="adm-search"
            style={{ flex: 1 }}
            placeholder="Establishment name..."
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
          />
          <button className="adm-btn-green" type="submit">+ Create</button>
        </form>
      </div>

      {/* List */}
      {establishments.length === 0 ? (
        <p className="muted-text" style={{ textAlign: "center", padding: 30 }}>No establishments yet.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {establishments.map((est) => {
            const isOpen = expanded === est._id;
            const estDjIds = est.djs.map((d) => d._id);
            const availableDjs = allDjs.filter((d) => !estDjIds.includes(d._id));

            return (
              <div key={est._id} className="adm-card" style={{ padding: 0, overflow: "hidden" }}>
                {/* Header */}
                <div
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "14px 18px", cursor: "pointer", background: isOpen ? "var(--adm-bg-sec, #f8fafc)" : "transparent",
                  }}
                  onClick={() => setExpanded(isOpen ? null : est._id)}
                >
                  <div>
                    <span style={{ fontWeight: 700, fontSize: "1rem" }}>{est.name}</span>
                    <span className="muted-text" style={{ marginLeft: 10, fontSize: "0.8rem" }}>
                      {est.djs.length} Talent
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <a
                      href={getQrUrl(est.slug)}
                      target="_blank"
                      rel="noreferrer"
                      className="adm-btn-sm adm-btn-outline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Open page
                    </a>
                    <button
                      className="adm-btn-sm adm-btn-red-sm"
                      onClick={(e) => { e.stopPropagation(); handleDelete(est._id); }}
                    >
                      Delete
                    </button>
                    <span style={{ fontSize: "0.9rem", color: "#94a3b8" }}>{isOpen ? "▲" : "▼"}</span>
                  </div>
                </div>

                {/* Expanded content */}
                {isOpen && (
                  <div style={{ padding: "0 18px 18px", borderTop: "1px solid var(--adm-line, #e5e7eb)" }}>
                    {/* QR Code */}
                    <div style={{ display: "flex", gap: 20, margin: "16px 0", alignItems: "center" }}>
                      <img
                        src={getQrImageUrl(est.slug)}
                        alt="QR Code"
                        style={{ width: 140, height: 140, borderRadius: 8, border: "1px solid #e5e7eb" }}
                      />
                      <div>
                        <p style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: 4 }}>QR Code URL:</p>
                        <code style={{ fontSize: "0.78rem", color: "var(--adm-blue)", wordBreak: "break-all" }}>
                          {getQrUrl(est.slug)}
                        </code>
                        <br />
                        <button
                          className="adm-btn-sm adm-btn-outline"
                          style={{ marginTop: 10 }}
                          onClick={() => {
                            const link = document.createElement("a");
                            link.href = getQrImageUrl(est.slug);
                            link.download = `qr-${est.slug}.png`;
                            link.click();
                          }}
                        >
                          Download QR
                        </button>
                      </div>
                    </div>

                    {/* Add Talent */}
                    <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                      <select
                        className="adm-select"
                        style={{ flex: 1 }}
                        value={addDjId}
                        onChange={(e) => setAddDjId(e.target.value)}
                      >
                        <option value="">— Select Talent to add —</option>
                        {availableDjs.map((d) => (
                          <option key={d._id} value={d._id}>{d.name} ({d.email})</option>
                        ))}
                      </select>
                      <button
                        className="adm-btn-green"
                        onClick={() => handleAddDj(est._id)}
                        disabled={!addDjId}
                      >
                        Add Talent
                      </button>
                    </div>

                    {/* DJ list */}
                    {est.djs.length === 0 ? (
                      <p className="muted-text" style={{ fontSize: "0.85rem" }}>No Talent added yet.</p>
                    ) : (
                      <table className="adm-table-light adm-table-compact">
                        <thead>
                          <tr>
                            <th>Talent</th>
                            <th>Price</th>
                            <th style={{ width: 80 }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {est.djs.map((dj) => {
                            const avatar = fmtAvatar(dj.avatarUrl);
                            return (
                              <tr key={dj._id}>
                                <td>
                                  <div className="dj-cell dj-cell-compact">
                                    {avatar ? (
                                      <img src={avatar} className="dj-cell-avatar-sm" alt="" />
                                    ) : (
                                      <div className="dj-cell-placeholder-sm">
                                        {dj.name?.[0]?.toUpperCase() || "?"}
                                      </div>
                                    )}
                                    <div>
                                      <span style={{ fontWeight: 600, fontSize: "0.85rem" }}>{dj.name}</span>
                                      {dj.slug && (
                                        <div className="muted-text" style={{ fontSize: "0.72rem" }}>@{dj.slug}</div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td style={{ fontWeight: 600 }}>
                                  {(dj.currency || "usd").toUpperCase()} {dj.pricePerRequest ?? 1}
                                </td>
                                <td>
                                  <button
                                    className="adm-btn-sm adm-btn-red-sm"
                                    onClick={() => handleRemoveDj(est._id, dj._id)}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
